import React, { useEffect, useState, type MouseEvent, useRef } from 'react'
import { useLocalStorage } from '@/lib/hooks/use-local-storage'
import { Notification } from '@/lib/notifications/notification-types'
import useNotificationStore from '@/lib/hooks/zustand-stores/use-notifications-store'
import { useRouter } from 'next/navigation'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { MessageSquare, Heart, Bell, Check, Reply, X, Building2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useOrganization } from '@clerk/nextjs'
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store'

interface NotificationPreviewProps {
  notification: Notification
  onClose: () => void
  bypassWebSocket?: boolean
  index?: number // Add index prop for positioning
}

const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
const DISPLAY_DURATION = 7000; // 10 seconds before auto-dismiss
const TOAST_HEIGHT = 100; // Adjusted height for better size balance
const TOAST_GAP = 10; // Gap between notifications
const USER_DOCK_HEIGHT = 58; // Estimated height of the user-dock (desktop)
const USER_DOCK_MOBILE_HEIGHT = 50; // Estimated height of the user-dock (mobile)
const TOP_MARGIN = 8; // Margin from the top
const EXIT_ANIMATION_DURATION = 100; // Fast dismissal animation

const NotificationPreview: React.FC<NotificationPreviewProps> = ({
  notification,
  onClose,
  bypassWebSocket = false,
  index = 0 // Default to 0 if not provided
}) => {
  const [showPreviews, setShowPreviews] = useLocalStorage(
    'showNotificationPreviews',
    true
  )
  const [exiting, setExiting] = useState(false)
  const [isRead, setIsRead] = useState(notification.isRead)
  const markAsRead = useNotificationStore(state => state.markAsRead)
  const router = useRouter()
  const cardRef = useRef<HTMLDivElement>(null)
  const { organization } = useOrganization()
  const userOrganizations = useUserStore(state => state.user?.organizations || [])
  const isFromDifferentOrg = notification.organizationId && organization?.id && 
                           notification.organizationId !== organization.id

  const [shownNotifications, setShownNotifications] = useLocalStorage<{ [key: string]: { timestamp: number } }>(
    'shownNotifications',
    {}
  );

  // Calculate top position based on index and user-dock height
  const stackPosition = index * (TOAST_HEIGHT + TOAST_GAP);
  const mobileTopPosition = USER_DOCK_MOBILE_HEIGHT + TOP_MARGIN + stackPosition;
  const desktopTopPosition = USER_DOCK_HEIGHT + TOP_MARGIN + stackPosition;
  
  // Make sure notifications are properly layered with z-index
  const zIndex = 45 - index; // Higher for newer notifications
  
  useEffect(() => {
    const checkIfAlreadyShown = () => {
      if (bypassWebSocket) return false; // Skip cache check for test notifications
      
      const notificationData = shownNotifications[notification._id];

      if (notificationData) {
        const timeSinceShown = Date.now() - notificationData.timestamp;
        if (timeSinceShown < CACHE_EXPIRY) {
          return true;
        } else {
          const updatedNotifications = { ...shownNotifications };
          delete updatedNotifications[notification._id];
          setShownNotifications(updatedNotifications);
        }
      }
      return false;
    };

    const markAsShown = () => {
      if (bypassWebSocket) return; // Skip cache for test notifications
      
      const updatedNotifications = {
        ...shownNotifications,
        [notification._id]: { timestamp: Date.now() }
      };
      setShownNotifications(updatedNotifications);
    };

    if (!checkIfAlreadyShown() && showPreviews) {
      markAsShown();
      // Auto dismiss after display duration
      const timer = setTimeout(handleDismiss, DISPLAY_DURATION);
      return () => clearTimeout(timer);
    } else {
      onClose(); // Automatically close if it was already shown
    }
  }, [onClose, showPreviews, notification._id, shownNotifications, setShownNotifications, bypassWebSocket]);

  const handleDismiss = (e?: MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent click from propagating
    }
    
    setExiting(true);
    setTimeout(() => {
      onClose();
    }, EXIT_ANIMATION_DURATION); // Reduced time for faster dismissal
  };

  const handleMarkAsRead = (e: MouseEvent) => {
    e.stopPropagation(); // Prevent click from propagating
    
    // Mark as read in store first
    markAsRead([notification._id]);
    
    // Then update local state
    setIsRead(true);
    
    // Immediately dismiss the notification
    handleDismiss();
  };

  const handleReply = (e: MouseEvent) => {
    e.stopPropagation(); // Prevent click from propagating
    
    // Mark as read first
    if (!isRead) {
      markAsRead([notification._id]);
      setIsRead(true);
    }
    
    // Navigate to the chat if it's a message notification
    if (notification.chatId) {
      handleDismiss(); // Dismiss immediately
      router.push(`/chat/${notification.chatId}`);
    }
  };

  const handleCardClick = () => {
    // Mark as read when clicked
    if (!isRead) {
      markAsRead([notification._id]);
      setIsRead(true);
    }
    
    // Dismiss immediately
    handleDismiss();
    
    // Then navigate to the notification link
    if (notification.link) {
      router.push(notification.link);
    }
  };

  // Get notification type details for styling
  const getTypeDetails = () => {
    const type = notification.type;
    
    if (type.includes('MENTION')) {
      return {
        icon: <Bell className={cn("h-3.5 w-3.5", isRead ? "text-muted-foreground" : "text-amber-500")} />,
        bgColor: isRead ? "bg-background/90" : "bg-amber-500/5 dark:bg-amber-500/10",
        borderColor: isRead ? "border-muted" : "border-amber-500/20",
        dotColor: "bg-amber-500",
        hoverColor: "hover:bg-amber-500/10 hover:text-amber-500",
      };
    } else if (type.includes('MESSAGE')) {
      return {
        icon: <MessageSquare className={cn("h-3.5 w-3.5", isRead ? "text-muted-foreground" : "text-blue-500")} />,
        bgColor: isRead ? "bg-background/90" : "bg-blue-500/5 dark:bg-blue-500/10",
        borderColor: isRead ? "border-muted" : "border-blue-500/20",
        dotColor: "bg-blue-500",
        hoverColor: "hover:bg-blue-500/10 hover:text-blue-500",
      };
    } else if (type.includes('FOLLOW') || type.includes('LIKE')) {
      return {
        icon: <Heart className={cn("h-3.5 w-3.5", isRead ? "text-muted-foreground" : "text-rose-500")} />,
        bgColor: isRead ? "bg-background/90" : "bg-rose-500/5 dark:bg-rose-500/10",
        borderColor: isRead ? "border-muted" : "border-rose-500/20",
        dotColor: "bg-rose-500",
        hoverColor: "hover:bg-rose-500/10 hover:text-rose-500",
      };
    } else {
      // Default for other notification types
      return {
        icon: <Bell className={cn("h-3.5 w-3.5", isRead ? "text-muted-foreground" : "text-primary")} />,
        bgColor: isRead ? "bg-background/90" : "bg-primary/5 dark:bg-primary/10",
        borderColor: isRead ? "border-muted" : "border-primary/20",
        dotColor: "bg-primary",
        hoverColor: "hover:bg-primary/10 hover:text-primary",
      };
    }
  };

  const typeDetails = getTypeDetails();
  const displayType = notification.type.split('_')[0]; // First part of the notification type
  
  // Find organization details from user store if available
  const getOrgDetails = () => {
    if (!notification.organizationId || !isFromDifferentOrg) return null
    
    const orgData = userOrganizations.find(org => org.id === notification.organizationId)
    
    return orgData ? {
      name: orgData.name,
      imageUrl: orgData.logo_url || orgData.image_url
    } : {
      name: notification.projectName || 'Other Org',
      imageUrl: null
    }
  }
  
  const orgDetails = getOrgDetails()

  if (!showPreviews) return null;

  const formatTime = (date: Date | string) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffMs = now.getTime() - notificationDate.getTime();
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h`;
    return `${Math.floor(diffMins / 1440)}d`;
  };

  return (
    <div 
      className={cn(
        "fixed z-[39] pointer-events-auto w-[92%] max-w-sm mx-auto left-0 right-0 sm:mx-0 sm:left-auto sm:right-4",
        "transition-all",
        "top-[var(--mobile-top,0)] sm:top-[var(--desktop-top,0)]"
      )}
      style={{ 
        ["--mobile-top" as any]: `${mobileTopPosition}px`,
        ["--desktop-top" as any]: `${desktopTopPosition}px`,
        zIndex: zIndex
      }}
    >
      <Card
        ref={cardRef}
        className={cn(
          "border w-full transition-all",
          exiting ? "opacity-0 translate-x-5 duration-100" : "opacity-100 translate-x-0", 
          "animate-in slide-in-from-right-5",
          "hover:shadow-md cursor-pointer group",
          typeDetails.bgColor,
          typeDetails.borderColor,
          "backdrop-blur-lg" // Enhanced blur effect
        )}
        onClick={handleCardClick}
      >
        <CardContent className="p-3">
          <div className="flex items-start gap-2.5">
            {/* Avatar with notification type indicator */}
            <div className="relative flex-shrink-0">
              <Avatar className={cn("h-8 w-8 border", typeDetails.borderColor)}>
                <AvatarImage 
                  src={notification.userPhoto} 
                  alt={notification.userName || 'User'} 
                />
                <AvatarFallback className="text-[11px]">{notification.userName?.substring(0, 2).toUpperCase() || 'UN'}</AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 rounded-full bg-background p-[2px] dark:bg-background">
                {typeDetails.icon}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 space-y-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-medium text-xs truncate max-w-[120px]">{notification.userName || 'User'}</p>
                <Badge variant="outline" className="px-1 h-4 text-[10px] font-normal">
                  {displayType}
                </Badge>
                
                {/* Add organization badge for cross-org notifications */}
                {isFromDifferentOrg && (
                  <Badge variant="outline" className="px-1 h-4 text-[10px] font-normal bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800 flex items-center">
                    {orgDetails?.imageUrl ? (
                      <Avatar className="h-2.5 w-2.5 mr-0.5">
                        <AvatarImage src={orgDetails.imageUrl} alt={orgDetails.name} />
                        <AvatarFallback className="text-[6px]"><Building2 className="h-1.5 w-1.5" /></AvatarFallback>
                      </Avatar>
                    ) : (
                      <Building2 className="h-2 w-2 mr-0.5" />
                    )}
                    {orgDetails?.name || 'Other Org'}
                  </Badge>
                )}
                
                {!isRead && <span className={cn("h-1.5 w-1.5 rounded-full", typeDetails.dotColor)}></span>}
              </div>
              
              {/* Message text with truncation and hover expansion */}
              <div className="relative">
                <p className={cn(
                  "text-[11px] leading-tight text-foreground",
                  "line-clamp-2 group-hover:line-clamp-none transition-all duration-200",
                  "max-h-[2.5em] group-hover:max-h-24"
                )}>
                  <span className={cn(isRead ? "font-normal" : "font-medium")}>
                    {notification.message.split(/(".*?(?:"|\.\.\.))/).map((part, index) =>
                      (part.startsWith('"') && (part.endsWith('"') || part.endsWith('...'))) ? (
                        <span key={index} className="font-bold text-black dark:text-white">
                          {part}
                        </span>
                      ) : (
                        <span key={index} className="text-muted-foreground">
                          {part}
                        </span>
                      )
                    )}
                  </span>
                </p>
              </div>
              
              <div className="flex justify-between items-center">
                <p className="text-[10px] text-muted-foreground">
                  {formatTime(notification.createdAt)}
                </p>
                
                {/* Actions */}
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-6 w-6 rounded-full p-0", 
                      typeDetails.hoverColor
                    )}
                    onClick={handleMarkAsRead}
                  >
                    <Check className="h-3.5 w-3.5" />
                    <span className="sr-only">Mark as read</span>
                  </Button>

                  {notification.type.includes('MESSAGE') && notification.chatId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-6 w-6 rounded-full p-0",
                        typeDetails.hoverColor
                      )}
                      onClick={handleReply}
                    >
                      <Reply className="h-3.5 w-3.5" />
                      <span className="sr-only">Reply</span>
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-6 w-6 rounded-full p-0",
                      "hover:bg-destructive/10 hover:text-destructive"
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDismiss(e);
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                    <span className="sr-only">Dismiss</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationPreview; 